/**
 * Same-origin API client. Every request goes to `/api/...`; the Pages Function (production) or the Vite proxy
 * (local dev) forwards it to the Railway API with the `/api` prefix stripped. The session is an HttpOnly cookie the
 * browser sends by itself; a 401 anywhere sends the browser to /login (see App).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    const code =
      typeof body === 'object' && body !== null && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null;
    super(code ? `API ${status}: ${code}` : `API ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** The endpoint does not exist yet (a Phase 4–5 route) rather than a missing record. */
  get isNotAvailable(): boolean {
    return this.status === 404 && (this.code === null || this.code === 'not_found');
  }
}

/** A 401: the session is missing or expired. The client has already sent the browser to /login. */
export class AuthRequiredError extends ApiError {
  constructor() {
    super(401, { error: 'unauthorized' });
    this.name = 'AuthRequiredError';
  }
}

/** The CSRF header the API requires on every state-changing request (sent on every request for simplicity). */
export const CSRF_HEADER = 'X-Studio-Request';

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** A 401 is an answer here (/auth/me, /auth/login), not a lost session: do not redirect to /login. */
  allowUnauthorized?: boolean;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

let fetchImpl: FetchLike = (input, init) => fetch(input, init);
let onUnauthorized: () => void = () => {};

/** Test seam (fetch) and the app's 401 handler (send the browser to /login). */
export function setApiTransport(next: { fetch?: FetchLike; onUnauthorized?: () => void } | null): void {
  fetchImpl = next?.fetch ?? ((input, init) => fetch(input, init));
  onUnauthorized = next?.onUnauthorized ?? (() => {});
}

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export function apiUrl(path: string): string {
  return `/api${path.startsWith('/') ? path : `/${path}`}`;
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { [CSRF_HEADER]: '1' };
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (opts.signal) init.signal = opts.signal;
  // The API's CSRF guard wants a JSON content type on every mutation, with or without a body.
  if (method !== 'GET') headers['content-type'] = 'application/json';
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  const res = await fetchImpl(apiUrl(path), init);
  if (res.status === 401) {
    if (!opts.allowUnauthorized) onUnauthorized();
    throw new AuthRequiredError();
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}
